// IM-UX-001 (IM-UI-079) — textos de contextualização dos módulos de estratégia. Um só lugar para a pergunta
// comercial, a metodologia em linguagem simples e a explicação completa (usados na lista e no detalhe de cada módulo).
// Os textos descrevem o comportamento real do InteliMove; nada aqui é dado de cliente.
import { ENV_FACT_KIND_HINT, ENV_FACT_KIND_LABEL, ENV_FACT_KINDS, FORCE_RULE_TEXT, PESTEL_CATEGORIES, PESTEL_LABEL, PORTER_FORCES, PORTER_LABEL, SEGMENT_THRESHOLDS, SIZING_METHOD_HINT, SIZING_METHOD_LABEL, SIZING_METHODS } from '@intelimove/contracts';
import type { ModuleGuide } from '../components/Guidance';

export const SIZING_GUIDE: ModuleGuide = {
  question: 'Qual é o tamanho do mercado que sua empresa pode atender?',
  method: 'TAM, SAM e SOM',
  summary: 'TAM, SAM e SOM estimam o mercado total, a parte compatível com sua oferta e a parcela que sua empresa pode realisticamente conquistar.',
  purpose: 'Definir metas comerciais, priorizar regiões e justificar investimento com números que têm base.',
  output: 'Três níveis em faixas (conservador, provável e superior), com a confiança e o que falta para cada um.',
  automatic: 'Busca a contagem oficial de empresas do IBGE quando há atividade econômica (CNAE) no escopo e usa as empresas que você já identificou.',
  review: 'Premissas que só a sua empresa conhece: parcela alcançável, valor por cliente (ticket) e capacidade comercial.',
  details: (
    <div className="gd-two">
      <div>
        <h3 className="gd-h3">O que cada sigla significa</h3>
        <ul className="sz-list">
          <li><b>TAM</b> (<i>Total Addressable Market</i>) — mercado total potencial: todas as empresas que poderiam comprar este tipo de solução.</li>
          <li><b>SAM</b> (<i>Serviceable Available Market</i>) — mercado compatível: a parte do TAM que combina com as suas ofertas, região e perfil de cliente (ICP).</li>
          <li><b>SOM</b> (<i>Serviceable Obtainable Market</i>) — mercado realisticamente alcançável: a parcela do SAM que dá para conquistar no período.</li>
        </ul>
        <p className="cell-sub wide">Nenhum número nasce sem premissa com fonte. Sem base, o nível aparece como “dados insuficientes” — nunca como zero.</p>
      </div>
      <div>
        <h3 className="gd-h3">Como o cálculo pode ser feito</h3>
        <ul className="sz-list">{SIZING_METHODS.map((m) => <li key={m}><b>{SIZING_METHOD_LABEL[m]}</b> — {SIZING_METHOD_HINT[m]}</li>)}</ul>
        <p className="cell-sub wide">A escolha da metodologia fica em “Personalizar análise” (na criação) e em “Ajustar metodologia e premissas” (na estimativa).</p>
      </div>
    </div>
  ),
};

export const ENVIRONMENT_GUIDE: ModuleGuide = {
  question: 'O que pode afetar seu mercado e sua capacidade de competir?',
  method: 'PESTEL e Cinco Forças de Porter',
  summary: 'A análise PESTEL avalia fatores externos que podem afetar seu mercado. As Cinco Forças de Porter avaliam a intensidade competitiva e a atratividade do setor.',
  purpose: 'Antecipar oportunidades e ameaças e decidir onde a concorrência pressiona preço e margem.',
  output: 'Fatores de maior impacto, oportunidades, ameaças, intensidade de cada força, consequências e ações a considerar.',
  automatic: 'Organiza as evidências que já existem no InteliMove (sinais, publicações, análises de mercado, concorrentes, dados oficiais) e aponta lacunas.',
  review: 'Confirmar ou rejeitar fatores, avaliar impacto quando não há base e transformar achados em riscos, oportunidades ou decisões.',
  details: (
    <div className="gd-two">
      <div>
        <h3 className="gd-h3">PESTEL — seis fatores externos</h3>
        <p className="cell-sub wide">{PESTEL_CATEGORIES.map((c) => PESTEL_LABEL[c]).join(', ')}. Cada fator responde: o que está acontecendo, por que importa, qual o impacto, qual ação considerar e quais evidências sustentam.</p>
        <h3 className="gd-h3">Cinco Forças de Porter</h3>
        <p className="cell-sub wide">{PORTER_FORCES.map((f) => PORTER_LABEL[f]).join('; ')}. {FORCE_RULE_TEXT}</p>
      </div>
      <div>
        <h3 className="gd-h3">Como ler cada afirmação</h3>
        <ul className="sz-list">{ENV_FACT_KINDS.map((k) => <li key={k}><b>{ENV_FACT_KIND_LABEL[k]}</b> — {ENV_FACT_KIND_HINT[k]}</li>)}</ul>
        <p className="cell-sub wide">Conhecimento geral de IA não entra como evidência. Onde não há dados, a categoria aparece como lacuna, com o que falta.</p>
      </div>
    </div>
  ),
};

export const BATTLECARD_GUIDE: ModuleGuide = {
  question: 'Como competir com este concorrente?',
  method: 'Battlecard',
  summary: 'Um Battlecard é um guia comercial que ajuda sua equipe a competir contra uma empresa específica.',
  purpose: 'Preparar reuniões e propostas: quando o concorrente ganha, quando perde e o que perguntar e responder.',
  output: 'Forças, vulnerabilidades, perguntas de descoberta, argumentos de diferenciação, objeções prováveis com respostas, riscos e oportunidades.',
  automatic: 'Monta o guia a partir da pesquisa do concorrente, das análises de mercado, da SWOT, dos sinais e do contexto confirmado da sua empresa.',
  review: 'Só as exceções: conflitos, baixa confiança, falta de evidência, informação antiga ou de alto impacto. O que está bem sustentado já entra em uso, identificado como validado automaticamente.',
  details: (
    <div className="gd-two">
      <div>
        <h3 className="gd-h3">Como cada item é classificado</h3>
        <ul className="sz-list">
          <li><b>Fato</b> — sustentado por fonte de terceiro ou registro oficial.</li>
          <li><b>Declarado</b> — o que a própria empresa diz de si (site, materiais).</li>
          <li><b>Indício</b> — evidência razoável, não definitiva.</li>
          <li><b>Hipótese</b> — interpretação ou regra do InteliMove, a validar.</li>
          <li><b>Não encontrado</b> — procurado nas fontes analisadas e não localizado.</li>
        </ul>
      </div>
      <div>
        <h3 className="gd-h3">Versões</h3>
        <p className="cell-sub wide">"Atualizar pesquisa" busca evidências novas e cria uma versão só se houver mudança relevante; as anteriores ficam como estavam, somente leitura. Edições, confirmações, rejeições e arquivamentos da equipe são carregados para a versão seguinte.</p>
        <h3 className="gd-h3">Validação</h3>
        <p className="cell-sub wide">Validado automaticamente = regra objetiva com evidência (ex.: publicado no site oficial, duas fontes independentes). Confirmado pelo usuário = decisão humana. Hipótese estratégica = interpretação usada como hipótese, nunca como fato.</p>
        <p className="cell-sub wide">Somente itens confirmados saem do InteliMove numa integração; observações internas nunca circulam.</p>
      </div>
    </div>
  ),
};

export const SEGMENTATION_GUIDE: ModuleGuide = {
  question: 'Quais segmentos sua empresa deve priorizar?',
  method: 'Segmentação e atratividade de mercado',
  summary: 'O InteliMove compara a atratividade dos segmentos considerando aderência à sua empresa, potencial comercial, concorrência, capacidade de entrega e evidências disponíveis.',
  purpose: 'Decidir onde concentrar prospecção, marketing e equipe comercial.',
  output: 'Ranking com nota de 0 a 100 e classe (prioritário, promissor, a observar), matriz de atratividade, comparação e recomendações.',
  automatic: 'Monta os segmentos a partir do contexto confirmado, das empresas-alvo, dos sinais verificáveis e de onde os concorrentes atuam, e calcula cada nota.',
  review: 'Confirmar ou rejeitar segmentos, avaliar critérios próprios da equipe e ajustar pesos quando a prioridade da empresa for outra.',
  details: (
    <div className="gd-two">
      <div>
        <h3 className="gd-h3">Como a nota é formada</h3>
        <p className="cell-sub wide">Nota = soma das contribuições (peso normalizado × nota de cada critério) dos critérios com dados. Critério sem dados fica fora da conta do segmento e reduz a cobertura — nunca vira zero.</p>
        <p className="cell-sub wide">Classes: prioritário com nota ≥ {SEGMENT_THRESHOLDS.prioritario}, demanda observada e boa cobertura; promissor ≥ {SEGMENT_THRESHOLDS.promissor}; a observar ≥ {SEGMENT_THRESHOLDS.observar}; “dados insuficientes” quando a cobertura é baixa.</p>
      </div>
      <div>
        <h3 className="gd-h3">O que é fato e o que é inferência</h3>
        <p className="cell-sub wide">Cada critério mostra o estado da evidência: <b>observado</b> (empresas, sinais, contexto declarado ou avaliação da equipe), <b>inferido</b> (dedução a partir do contexto — trate como hipótese) ou <b>sem evidência suficiente</b> (fica fora da conta, não vale zero). Recomendações inferidas pedem validação.</p>
        <p className="cell-sub wide">Critérios, pesos e região ficam em “Ajustar critérios, pesos e região” (configurações avançadas). As mudanças ficam pendentes até “Recalcular análise”, que grava uma versão nova e preserva a anterior. A comparação começa automática (primeiros do ranking) e vira personalizada quando você escolhe os segmentos.</p>
      </div>
    </div>
  ),
};

// IM-EXT-019 (IM-UI-084) — Portfólio e Ecossistema.
export const PORTFOLIO_GUIDE: ModuleGuide = {
  question: 'Quais ofertas priorizar, onde estão as lacunas, como estão os preços e quem pode ampliar seu alcance?',
  method: 'Matriz ofertas × mercado, comparabilidade de preços e mapa de ecossistema',
  summary: 'Relaciona cada oferta aos ICPs, segmentos, necessidades, concorrentes, substitutos e parceiros; compara preços só quando são comparáveis; e separa parcerias existentes de sugestões.',
  purpose: 'Decidir o que fortalecer, combinar, reposicionar ou cobrir no portfólio e onde preço e parcerias pesam na venda.',
  output: 'Resposta executiva, matriz de cobertura, lacunas, sobreposições, concorrência por oferta, posição de preço, oportunidades de ecossistema e recomendações priorizadas.',
  automatic: 'Reúne contexto, segmentação, concorrentes, battlecards, análises de mercado e preços e relações registrados. Sem IA e sem pesquisa nova.',
  review: 'Confirmar preços e relações, informar a equivalência dos preços, declarar diferenciais e decidir quais recomendações a equipe assume.',
  details: (
    <div className="gd-two">
      <div>
        <h3 className="gd-h3">Como ler cada relação</h3>
        <ul className="sz-list">
          <li><b>✓ Confirmada</b>: declarada no contexto confirmado ou sustentada por fonte.</li>
          <li><b>≈ Inferida</b>: interpretada por termos em comum. Trate como hipótese.</li>
          <li><b>? Sem evidência suficiente</b>: falta base. Isso não quer dizer que a relação não exista.</li>
          <li><b>— Não aplicável</b>: por exemplo, regiões sem interseção.</li>
        </ul>
        <h3 className="gd-h3">Lacunas</h3>
        <p className="cell-sub wide">Confirmada: necessidade evidenciada sem oferta adequada. Provável: há indícios, mas faltam evidências. Hipótese de expansão: sugestão ainda não validada.</p>
      </div>
      <div>
        <h3 className="gd-h3">Preços</h3>
        <p className="cell-sub wide">Só entram preços internos confirmados e preços públicos com página de origem. “Sob consulta” e “não publicado” não têm valor e nunca contam como zero. Antes de comparar, o InteliMove confere equivalência, unidade, moeda, recorrência, escopo e condições. Preços incompatíveis não entram em ranking. A conversão para valor mensal serve só para leitura, e o valor original é sempre mantido.</p>
        <h3 className="gd-h3">Ecossistema</h3>
        <p className="cell-sub wide">Relações declaradas, confirmadas e indícios ficam separados das sugestões do InteliMove (parceiro recomendado, oportunidade de canal). Sugestão nunca aparece como parceria atual, e nenhum contato é iniciado.</p>
      </div>
    </div>
  ),
};

// IM-EXT-020 — Parecer Executivo e Central de Decisões.
export const EXECUTIVE_GUIDE: ModuleGuide = {
  question: 'Qual estratégia comercial o InteliMove recomenda neste momento?',
  method: 'Consolidação das análises mais recentes, com agrupamento e conflitos por regra',
  summary: 'Junta segmentação, TAM/SAM/SOM, PESTEL, Cinco Forças, SWOT, battlecards, portfólio, preços, ecossistema e sinais em um parecer só, com a evidência de cada resposta.',
  purpose: 'Saber o que importa agora: para quem vender, como competir, quais riscos acompanhar e quais decisões precisam ser tomadas.',
  output: 'Resumo executivo, prioridades, posicionamento, riscos e oportunidades, decisões necessárias, lacunas e metodologia, com versões comparáveis.',
  automatic: 'Usa a versão concluída mais recente de cada análise, agrupa recomendações equivalentes, aponta conflitos e propõe decisões. Sem IA e sem pesquisa nova.',
  review: 'Ler o parecer, resolver lacunas e deliberar as decisões propostas na Central de Decisões.',
  details: (
    <div className="gd-two">
      <div>
        <h3 className="gd-h3">Como o parecer é montado</h3>
        <ul className="sz-list">
          <li>Recomendações sobre o mesmo alvo e na mesma direção viram um grupo (idênticas ou equivalentes).</li>
          <li>Direções opostas sobre o mesmo alvo viram um conflito explícito, nunca resolvido em silêncio.</li>
          <li>Hipóteses aparecem como hipóteses; falta de dado aparece como lacuna, nunca como resultado negativo.</li>
        </ul>
      </div>
      <div>
        <h3 className="gd-h3">O que o parecer não faz</h3>
        <p className="cell-sub wide">Não decide pela organização, não executa ações, não altera preços, ofertas ou o CERES CRM e não envia mensagens. Cada nova geração cria uma versão nova só quando algo mudou.</p>
      </div>
    </div>
  ),
};
export const LEARNING_GUIDE: ModuleGuide = {
  question: 'O que os resultados comerciais ensinam sobre a estratégia da empresa?',
  method: 'Esperado × realizado, benchmarks internos e públicos com fonte, amostra explícita e ajustes com aprovação humana',
  summary: 'O InteliMove verifica se as recomendações funcionaram: compara o previsto com o que aconteceu, explica a diferença e propõe ajustes.',
  purpose: 'Fechar o ciclo análise → decisão → resultado → aprendizado → nova versão da estratégia.',
  output: 'Resultados, comparações, avaliação de decisões e simulações, premissas a recalibrar, aprendizados e ajustes recomendados.',
  automatic: 'Calcula indicadores, compara períodos e grupos, detecta desvios e propõe ajustes. Nunca altera estratégia, pesos ou histórico sozinho.',
  review: 'Associar resultados sem vínculo, aprovar, editar, rejeitar ou adiar ajustes. Aplicar cria versão nova; a anterior fica preservada.',
  details: (
    <div className="gd-two">
      <div>
        <h3 className="gd-h3">Amostra antes de conclusão</h3>
        <p className="cell-sub wide">Com poucos registros, os números são descritivos e aparecem como indício. Uma oportunidade isolada nunca vira benchmark.</p>
      </div>
      <div>
        <h3 className="gd-h3">Comparação honesta</h3>
        <p className="cell-sub wide">Só compara indicadores com a mesma definição. Média de mercado não é garantia de desempenho individual, e correlação não é causa.</p>
      </div>
    </div>
  ),
};

export const SCENARIOS_GUIDE: ModuleGuide = {
  question: 'Qual alternativa estratégica apresenta a melhor relação entre oportunidade, risco e capacidade de execução?',
  method: 'Cenário-base e alternativas com premissas explícitas, cálculo por regras, sensibilidade e teste de estresse',
  summary: 'Compare caminhos antes de decidir: o que pode acontecer se escolhermos este caminho? Cenário não é previsão — é uma hipótese estruturada.',
  purpose: 'Mostrar o que é conhecido, o que foi assumido, o que foi escolhido, o que pode variar e quais resultados dependem de cada premissa.',
  output: 'Comparação, cenário recomendado (do sistema) e cenário escolhido (da equipe), premissas sensíveis, ponto de ruptura, estresse e resultado esperado × realizado.',
  automatic: 'Propõe alternativas específicas, reaproveita o contexto e as análises, sugere premissas marcadas como tal, calcula, compara e recomenda.',
  review: 'Ajustar premissas com justificativa, escolher o cenário e levá-lo à Central de decisões. Nada é aprovado ou executado automaticamente.',
  details: (
    <div className="gd-two">
      <div>
        <h3 className="gd-h3">Fato, premissa, escolha</h3>
        <ul className="sz-list">
          <li><b>Fato</b>: tem fonte, data e análise de origem.</li>
          <li><b>Premissa</b>: valor usado para simular; não é fato.</li>
          <li><b>Escolha</b>: o caminho considerado no cenário.</li>
          <li><b>Restrição, incerteza e hipótese</b> ficam separadas e visíveis.</li>
        </ul>
      </div>
      <div>
        <h3 className="gd-h3">Números com honestidade</h3>
        <p className="cell-sub wide">Faixas (mínimo, provável, máximo) com fórmula e fonte. Sem entrada suficiente, não há número: aparece a lacuna. Avaliações qualitativas nunca viram dinheiro.</p>
      </div>
    </div>
  ),
};

export const DECISIONS_GUIDE: ModuleGuide = {
  question: 'Quais decisões estratégicas precisam ser tomadas, por quê e com que base?',
  method: 'Decisões propostas com prioridade explicada e registro humano versionado',
  summary: 'O InteliMove recomenda; a organização decide. Cada decisão mostra a recomendação, as alternativas, a evidência e quem decidiu, quando e por quê.',
  purpose: 'Deliberar o que importa, acompanhar o que foi decidido e revisar quando a base muda.',
  output: 'Visão executiva, prioridades, conflitos, decisões aguardando evidência, em execução, concluídas e histórico completo.',
  automatic: 'Propõe decisões, consolida recomendações, prioriza, sinaliza conflitos e pede revisão quando uma análise de origem muda.',
  review: 'Aprovar, aprovar com ajustes, rejeitar ou adiar, sempre com justificativa. Nenhuma decisão humana é revertida automaticamente.',
  details: (
    <div className="gd-two">
      <div>
        <h3 className="gd-h3">Recomendação, decisão e ação</h3>
        <ul className="sz-list">
          <li><b>Recomendação</b>: orientação do InteliMove.</li>
          <li><b>Decisão proposta</b>: questão que pede deliberação.</li>
          <li><b>Decisão tomada</b>: escolha registrada por pessoa autorizada.</li>
          <li><b>Ação</b>: próximo passo; aprovada não significa concluída.</li>
        </ul>
      </div>
      <div>
        <h3 className="gd-h3">Prioridade</h3>
        <p className="cell-sub wide">Impacto, urgência, confiança, quantidade de análises que convergem, atualidade, risco de não decidir e esforço. A faixa (decidir agora, decidir em breve, acompanhar, aguardar evidências) sempre vem com os motivos.</p>
      </div>
    </div>
  ),
};
